import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState, useLayoutEffect } from "react";
import { useLazyQuery } from "@apollo/client";

import "./DashboardLayout.scss";
import HeaderNav from "../../component/header-nav/HeaderNav";
import CollapsibleSidenav from "../../component/collapsible-sidenav/CollapsibleSidenav";
import {
  GET_LINKEDIN_LAST_POST,
  GET_USER_INFO,
} from "../../service/graphql/query";
import { PagePermissionMask } from "../../service/utils/interfaces";

// userCustomers is the GET_USER_INFO result, shared with the pages so they do
// not query it again on every navigation.
export type ContextType = { darkMode: boolean; userCustomers?: any[] };

// Set REACT_APP_LINKEDIN_ENABLED to "false" to skip the LinkedIn feed query and hide the embed
const isLinkedInEnabled =
  process.env.REACT_APP_LINKEDIN_ENABLED?.toLowerCase() !== "false";

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("<<Profile Name>>");
  const [permissionMask, setPermissionMask] = useState<PagePermissionMask>();
  const [isTippingUser, setIsTippingUser] = useState(false);
  const [sidenavCollapsed, setSidenavCollapsed] = useState(false);
  const [sidenavExpanding, setSidenavExpanding] = useState(false);
  const [isDarkMode, setDarkMode] = useState(true);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [featuredPostShareId, setFeaturedPostShareId] = useState("");

  const [getUserInfo, { data: userData }] = useLazyQuery(GET_USER_INFO, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });
  const [getLinkedInLastPost, { data: shareUrnData }] = useLazyQuery(
    GET_LINKEDIN_LAST_POST
  );

  useLayoutEffect(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    setWidth(width);
    setHeight(height);
  }, []);

  useEffect(() => {
    if (getUserInfo && getLinkedInLastPost) {
      const handleWindowResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWidth(width);
        setHeight(height);
      };

      getUserInfo({ context: { apiName: "middleware" } });
      if (isLinkedInEnabled)
        getLinkedInLastPost({ context: { apiName: "middleware" } });

      window.addEventListener("resize", handleWindowResize);
      return () => window.removeEventListener("resize", handleWindowResize);
    }
  }, [getUserInfo, getLinkedInLastPost]);

  useEffect(() => {
    if (sidenavExpanding) {
      setTimeout(() => {
        setSidenavCollapsed(false);
        setSidenavExpanding(false);
      }, 290);
    }
  }, [sidenavExpanding]);

  useEffect(() => {
    if (userData && userData.getCustomers && userData.getCustomers.length > 0) {
      manageUserData(userData);
    }
  }, [userData]);

  const manageUserData = async (userData: any) => {
    const user = userData.getCustomers[0];
    const name =
      user.displayUsername && user.displayUsername.length > 0
        ? user.displayUsername
        : user.contactName;
    setIsTippingUser(user.category === "Tipping");
    setDisplayName(name);
    setPermissionMask({
      financialPage: user.pageAuth.financialPage,
      weightingPage: user.pageAuth.weightingPage,
      sustainabilityAudit: user.pageAuth.sustainabilityAudit,
      reportingPage: user.pageAuth.reportingPage,
    });
  };

  useEffect(() => {
    if (
      shareUrnData &&
      shareUrnData.getLinkedinLastPost &&
      shareUrnData.getLinkedinLastPost.startsWith("urn")
    )
      setFeaturedPostShareId(shareUrnData.getLinkedinLastPost);
  }, [shareUrnData]);

  return (
    <div className="page-wrapper">
      <HeaderNav
        name={displayName}
        darkMode={isDarkMode}
        onThemeSwitch={() => setDarkMode(!isDarkMode)}
        screenWidth={width}
        screenHeight={height}
        permMask={permissionMask}
        isTippingUser={isTippingUser}
      />
      <div className="body">
        <CollapsibleSidenav
          collapsed={sidenavCollapsed}
          expanding={sidenavExpanding}
          darkMode={isDarkMode}
          onCollapseTrigger={() => {
            if (sidenavCollapsed) setSidenavExpanding(true);
            else setSidenavCollapsed(true);
          }}
          featuredPostShareID={featuredPostShareId}
          screenWidth={width}
          screenHeight={height}
          permMask={permissionMask}
          isTippingUser={isTippingUser}
        />
        <Outlet
          context={
            {
              darkMode: isDarkMode,
              userCustomers: userData?.getCustomers,
            } satisfies ContextType
          }
        />
      </div>
    </div>
  );
};

export const useDarkModeCheck = () => {
  return useOutletContext<ContextType>();
};
